import React, { useRef, useState } from 'react';
import axios from 'axios';
import { CircularProgress } from '@material-ui/core';

import { ServerImage } from '../../../../server/app';

import InPocketStyles from './InPocket.styles';
import { useEffect } from 'react';

const SCALE = 2.5;
const LEG_IMAGE_URL = '/images/pocket/front.png';
const OUTLINE_IMAGE_URL = '/images/pocket/front-underlay.png';
const POCKET_IMAGE_URL = '/images/pocket/front-overlay.png';

// Types
type Props = {
  device: ServerImage;
};

// Component
const InPocket: React.FC<Props> = ({ device }) => {
  // Local state
  const [loading, setLoading] = useState(true);
  const [legImage, setLegImage] = useState<string | undefined>();
  const [outlineImage, setOutlineImage] = useState<string | undefined>();
  const [pocketImage, setPocketImage] = useState<string | undefined>();
  const [deviceImage, setDeviceImage] = useState<string | undefined>();
  const [hover, setHover] = useState(false);

  // Refs
  const bgWidth = useRef(0);
  const bgHeight = useRef(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const legRef = useRef<HTMLImageElement>(null);
  const outlineRef = useRef<HTMLImageElement>(null);
  const pocketRef = useRef<HTMLImageElement>(null);
  const deviceRef = useRef<HTMLImageElement>(null);

  // Effects
  useEffect(() => {
    setLoading(true);

    // Load leg image
    const loadLegImage = new Image();

    loadLegImage.src = LEG_IMAGE_URL;
    loadLegImage.onload = () => {
      // Set BG sizes
      bgWidth.current = loadLegImage.naturalWidth / SCALE;
      bgHeight.current = loadLegImage.naturalHeight / SCALE;

      setLegImage(LEG_IMAGE_URL);
    };

    // Load outline image
    const loadOutlineImage = new Image();

    loadOutlineImage.src = OUTLINE_IMAGE_URL;
    loadOutlineImage.onload = () => setOutlineImage(OUTLINE_IMAGE_URL);

    // Load pocket image
    const loadPocketImage = new Image();

    loadPocketImage.src = POCKET_IMAGE_URL;
    loadPocketImage.onload = () => setPocketImage(POCKET_IMAGE_URL);
  }, []);

  useEffect(() => {
    if (legImage && outlineImage && pocketImage) {
      const container = containerRef.current;
      const leg = legRef.current;
      const outline = outlineRef.current;
      const pocket = pocketRef.current;

      if (container && leg && outline && pocket) {
        // Set sizes to scale
        container.style.width = `${bgWidth.current}px`;
        container.style.height = `${bgHeight.current}px`;
        leg.style.width = `${bgWidth.current}px`;
        leg.style.height = `${bgHeight.current}px`;
        outline.style.width = `${bgWidth.current}px`;
        outline.style.height = `${bgHeight.current}px`;
        pocket.style.width = `${bgWidth.current}px`;
        pocket.style.height = `${bgHeight.current}px`;
      }
    }
  }, [legImage, outlineImage, pocketImage]);

  useEffect(() => {
    if (legImage && outlineImage && pocketImage) {
      // Retrieve the device image
      const retrieveDeviceImage = async () => {
        const image = await axios.get(`/api/image/?path=devices/${device.path}`, {
          responseType: 'blob',
        });

        const imagePath = URL.createObjectURL(image.data);

        // Preload & set
        const tempImage = new Image();

        tempImage.src = imagePath;
        tempImage.onload = () => {
          setDeviceImage(imagePath);
        };
      };

      retrieveDeviceImage();
    }
  }, [device, legImage, outlineImage, pocketImage]);

  useEffect(() => {
    const deviceJS = deviceRef.current;

    if (deviceImage && device.meta.width && device.meta.height && deviceJS) {
      // Calculate device offset position
      // - Left: 34.4%
      // - Bottom: 27%
      // - Pocket width: 725
      const pocketCentral = 725 / SCALE / 2;
      const deviceWidth = device.meta.width / SCALE;

      // Middle of pocket (minus half of device width from center point)
      const deviceLeft = 0.344 * bgWidth.current + pocketCentral - deviceWidth / 2;

      deviceJS.style.width = `${deviceWidth}px`;
      deviceJS.style.height = `${device.meta.height / SCALE}px`;
      deviceJS.style.left = `${deviceLeft}px`;
      deviceJS.style.bottom = `${0.27 * bgHeight.current}px`;

      setLoading(false);
    }
  }, [deviceImage, device.meta.width, device.meta.height]);

  // Render
  return (
    <InPocketStyles hover={hover}>
      <div className="loading" hidden={!loading}>
        <CircularProgress />
      </div>

      <div className="container" ref={containerRef} hidden={loading}>
        <img src={legImage} ref={legRef} className="leg" alt="" />
        <img src={outlineImage} ref={outlineRef} className="outline" alt="" />
        <img
          src={deviceImage}
          ref={deviceRef}
          className="device"
          alt=""
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        />
        <img src={pocketImage} ref={pocketRef} className="pocket" alt="" />
      </div>
    </InPocketStyles>
  );
};

export default InPocket;
